import { useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { capitalDesignTokens } from '@opengov/capital-mui-theme';
import { getIconComponent } from '../../utils/icons';
import { TestIds } from '../../constants';
import type { Favorite } from '../../services/favoritesStorage';
import { useTruncated } from '../../hooks/useTruncated';

const StarIcon = getIconComponent('StarOutline');

interface FavoriteRowProps {
  favorite: Favorite;
  /** Closes the drawer after navigation, mirroring a regular sidebar nav item's click. */
  onNavigate?: () => void;
}

/**
 * A favorite in the sidebar: a flat link, intentionally identical in behavior to a regular
 * navigation item. All management (rename, remove, reorder) lives in the Favorites Manager,
 * so the sidebar stays a clean, low-friction list of shortcuts.
 */
export function FavoriteRow({ favorite, onNavigate }: FavoriteRowProps) {
  const labelRef = useRef<HTMLSpanElement>(null);
  const isTruncated = useTruncated(labelRef, favorite.label);

  return (
    <ListItem data-testid={TestIds.FAVORITES_ROW} disablePadding>
      <Tooltip
        title={isTruncated ? favorite.label : ''}
        arrow
        enterNextDelay={300}
        placement="right"
      >
        <ListItemButton
          component={Link}
          to={favorite.url}
          onClick={onNavigate}
          disableRipple
          sx={{ px: 2, py: 1, minHeight: 40, borderRadius: 2, mx: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <StarIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          </ListItemIcon>
          <ListItemText
            disableTypography
            primary={
              <Typography
                ref={labelRef}
                component="span"
                variant="body2"
                noWrap
                sx={{ display: 'block', color: capitalDesignTokens.foundations.colors.gray700 }}
              >
                {favorite.label}
              </Typography>
            }
            sx={{ minWidth: 0 }}
          />
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );
}
